import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "src/services";
import { type ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Constants } from "@eagerdog/constants";
import { IEventMessage, IClub } from "@eagerdog/interfaces";

import Button from "src/components/Button/Button";
import { AddContactModal } from 'src/components/AddContactModal/AddContactModal';
import { Modal, useModal } from "src/components/Modal/Modal";
import VolunteerSettingsModal from "src/components/VolunteerSettingsModal/VolunteerSettingsModal";
import EditCapacitiesModal from "src/components/EditCapacitiesModal/EditCapacitiesModal";
import Confirm from "src/components/Confirm/Confirm";
import SendMessage, { SendMessageType } from "src/components/SendMessage/SendMessage";
import { toast } from "src/components/Toast/ToastManager";
import { download } from "src/components/Download/DownloadManager";
import { useMessages } from "src/context/MessagesContext";

import { ActionsRenderer } from "./ActionsRenderer";
import { BannedUsersModal } from "../BannedUsersModal";

import styles from "./MembersTab.module.scss";

// Capacity colors (Primary/Vibrant scheme)
const capacityColors: Record<string, string> = {
    [Constants.capacity_type.participant]: '#3B82F6', // Blue
    [Constants.capacity_type.volunteer]: '#22C55E',   // Green
    [Constants.capacity_type.member]: '#8B5CF6',      // Purple
    [Constants.capacity_type.admin]: '#F97316',       // Orange
    [Constants.capacity_type.follower]: '#EC4899',    // Pink
    [Constants.capacity_type.donor]: '#808000',       // Olive
};

// Custom renderer for Roles column
const RolesRenderer: React.FC<{ data: IRow }> = ({ data }) => {
    return (
        <div className={styles.rolesBadges}>
            {data.capacities.map((capacity) => (
                <span 
                    key={capacity} 
                    className={styles.roleBadge}
                    style={{ backgroundColor: capacityColors[capacity] }}
                >
                    {capacity.charAt(0).toUpperCase() + capacity.slice(1)}
                </span>
            ))}
        </div>
    );
};

interface IProps {
    club: IClub
}

interface IRow {
    user_id: string,
    name: string,
    email: string,
    opted_in: boolean,
    roles: string,
    capacities: string[],
    actions: any
}


const MembersTab: React.FC<IProps> = (props) => {
    const navigate = useNavigate();
    const { initiateConversation } = useMessages();
    const { toggle: toggleAddContact, isShown: isAddContactShown } = useModal();
    const { toggle: toggleSettings, isShown: isSettingsShown } = useModal();
    const { toggle: toggleEdit, isShown: isEditShown } = useModal();
    const { toggle: toggleRemove, isShown: isRemoveShown } = useModal();
    const { toggle: toggleSendMessage, isShown: isSendMessageShown } = useModal();
    const { toggle: toggleSendIndividual, isShown: isSendIndividualShown } = useModal();
    const { toggle: toggleBannedUsers, isShown: isBannedUsersShown } = useModal();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [rowData, setRowData] = useState<IRow[]>([]);
    const [capacityCounts, setCapacityCounts] = useState<Record<string, number>>({
        [Constants.capacity_type.participant]: 0,
        [Constants.capacity_type.volunteer]: 0,
        [Constants.capacity_type.member]: 0,
        [Constants.capacity_type.admin]: 0,
        [Constants.capacity_type.follower]: 0,
        [Constants.capacity_type.donor]: 0
    });

    // Capacity filter state
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    
    const capacityFilters = useMemo(() => [
        { key: Constants.capacity_type.participant, label: 'Participant' },
        { key: Constants.capacity_type.volunteer, label: 'Volunteer' },
        { key: Constants.capacity_type.member, label: 'Member' },
        { key: Constants.capacity_type.admin, label: 'Admin' },
        { key: Constants.capacity_type.follower, label: 'Follower' },
        { key: Constants.capacity_type.donor, label: 'Donor' }
    ], []);

    const toggleFilter = (capacity: string) => {
        setSelectedFilters(prev => 
            prev.includes(capacity) 
                ? prev.filter(f => f !== capacity)
                : [...prev, capacity]
        );
        setLoaded(false);
    };

    const filteredRowData = useMemo(() => rowData, [rowData]);

    const [selectedUser, setSelectedUser] = useState<{
        user_id: string;
        name: string;
        email: string;
        currentCapacities: string[];
    } | null>(null);
    const [memberToRemove, setMemberToRemove] = useState<IRow | null>(null);
    const [isRemoving, setIsRemoving] = useState<boolean>(false);
    
    // Message state
    const [messageRecipient, setMessageRecipient] = useState<{
        user_id: string;
        name: string;
        email: string;
    } | null>(null);
    const [isSending, setIsSending] = useState<boolean>(false);
    
    // Opt-in counts for the Send Message modal
    const [optInCounts, setOptInCounts] = useState<{ total: number; optedIn: number; optedOut: number }>({ 
        total: 0, 
        optedIn: 0, 
        optedOut: 0 
    });

    // Fetch opt-in counts when opening the Send Message modal
    const fetchOptInCounts = useCallback(async () => {
        try {
            const counts = await apiService.getClubOptInCounts(
                props.club._id, 
                selectedFilters.length > 0 ? selectedFilters : undefined
            );
            setOptInCounts(counts);
        } catch (error) {
            // Default to showing all as opted out if fetch fails
            setOptInCounts({ total: filteredRowData.length, optedIn: 0, optedOut: filteredRowData.length });
        }
    }, [props.club._id, selectedFilters, filteredRowData.length]);

    // Open Send Message modal and fetch counts
    const handleOpenSendMessage = useCallback(async () => {
        if (!props.club.has_email_contacts_access) {
            toast.show({
                title: 'Premium Feature',
                content: 'Upgrade to premium to send messages to your contacts',
                type: 'fail',
                duration: 5000,
                actionLabel: 'Upgrade Now',
                onAction: () => navigate('/premium')
            });
            return;
        }
        await fetchOptInCounts();
        toggleSendMessage();
    }, [fetchOptInCounts, toggleSendMessage, props.club.has_email_contacts_access, navigate]);

    const handleEditMember = (row: IRow) => {
        setSelectedUser({
            user_id: row.user_id,
            name: row.name,
            email: row.email,
            currentCapacities: row.capacities
        });
        toggleEdit();
    };

    const handleRemoveMember = (row: IRow) => {
        setMemberToRemove(row);
        toggleRemove();
    };

    const confirmRemoveMember = async () => {
        if (!memberToRemove) return;

        setIsRemoving(true);

        try {
            // Remove every role in a single atomic call so a contact with
            // multiple roles (or only a "participant" role) can always be
            // fully removed in one shot.
            await apiService.removeAllClubCapacities(props.club._id, memberToRemove.user_id);

            toast.show({
                title: 'Success',
                content: `${memberToRemove.name} has been removed from club contacts`,
                type: 'success',
                duration: 3000
            });

            // Refresh table
            setLoaded(false);
            toggleRemove();
            setMemberToRemove(null);
        } catch (error: any) {
            console.error('Error removing contact:', error);
            toast.show({
                title: 'Error',
                content: error.response?.data?.message || 'Failed to remove contact',
                type: 'fail',
                duration: 5000
            });
        } finally {
            setIsRemoving(false);
        }
    };

    const handleSendMessage = (row: IRow) => {
        setMessageRecipient({
            user_id: row.user_id,
            name: row.name,
            email: row.email
        });
        toggleSendIndividual();
    };

    const handleSendDirectMessage = (row: IRow) => {
        initiateConversation(row.user_id, row.name, true);
    };

    const handleBulkSendMessage = async (formData: IEventMessage) => {
        setIsSending(true);

        try {
            const result = await apiService.sendBulkMessageToContacts(
                props.club._id,
                formData.custom_subject || 'Message from Club',
                formData.content,
                selectedFilters.length > 0 ? selectedFilters : undefined
            );

            toast.show({
                title: 'Message Sent',
                content: result.message,
                type: 'success',
                duration: 5000
            });

            toggleSendMessage();
        } catch (error: any) {
            toast.show({
                title: 'Send Failed',
                content: error.message || 'Failed to send message',
                type: 'fail',
                duration: 5000
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleIndividualSendMessage = async (formData: IEventMessage) => {
        if (!messageRecipient) {
            toast.show({
                title: 'Validation Error',
                content: 'Recipient required',
                type: 'fail',
                duration: 5000
            });
            return;
        }

        setIsSending(true);

        try {
            await apiService.sendIndividualMessageToContact(
                props.club._id,
                messageRecipient.user_id,
                formData.custom_subject || 'Message from Club',
                formData.content
            );

            toast.show({
                title: 'Message Sent',
                content: `Message sent to ${messageRecipient.name}`,
                type: 'success',
                duration: 5000
            });

            setMessageRecipient(null);
            toggleSendIndividual();
        } catch (error: any) {
            toast.show({
                title: 'Send Failed',
                content: error.message || 'Failed to send message',
                type: 'fail',
                duration: 5000
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleExportContacts = () => {
        if (!props.club.has_export_contacts_access) {
            toast.show({
                title: 'Premium Feature',
                content: 'Upgrade to premium to export your contacts',
                type: 'fail',
                duration: 5000,
                actionLabel: 'Upgrade Now',
                onAction: () => navigate('/premium')
            });
            return;
        }
        download.pushDownload({
            type: "club_contacts",
            clubId: props.club._id,
            capacityFilters: selectedFilters.length > 0 ? selectedFilters : undefined
        });
    };

    // Opt-In status renderer
    const OptInRenderer: React.FC<{ data: IRow }> = ({ data }) => {
        return (
            <div className={styles.optInStatus}>
                {data.opted_in ? (
                    <span className={styles.optedIn}>✓</span>
                ) : (
                    <span className={styles.optedOut}>✗</span>
                )}
            </div>
        );
    };

    let colDefs:ColDef<IRow>[] = [
        { field: "name", minWidth: 120, filter: "agTextColumnFilter" },
        { field: "email", minWidth: 180, filter: "agTextColumnFilter" },
        {
            field: "opted_in",
            headerName: "Opt-In",
            width: 100,
            minWidth: 100,
            maxWidth: 100,
            cellRenderer: OptInRenderer,
            headerTooltip: "Whether user has opted in to receive communications from this club"
        },
        { 
            field: "roles", 
            headerName: "Roles",
            minWidth: 300, 
            filter: true,
            cellRenderer: RolesRenderer
        },
        {
            headerName: "",
            field: "actions",
            maxWidth: 60,
            minWidth: 60,
            cellRenderer: ActionsRenderer,
            pinned: "right",
            cellRendererParams: {
                onEdit: handleEditMember,
                onRemove: handleRemoveMember,
                onSendMessage: handleSendMessage,
                onSendDirectMessage: handleSendDirectMessage
            },
            resizable: false,
            cellStyle: {
                "overflow": "visible",
                "zIndex": "2"
            }
        }
    ];

    const getCapacities = useCallback(async () => {
        try {
            let rows: IRow[] = [];
            const response = await apiService.getClubCapacities(
                props.club._id,
                undefined,
                1,
                1000,
                selectedFilters
            );
            
            for (let i in response.capacities) {
                let member = response.capacities[i];
                
                // Check if user has opted in to communications from this club
                // Handle both string and ObjectId formats by converting to strings
                const optedInClubs = ((member.user as any).opted_in_clubs || []).map((id: any) => id?.toString ? id.toString() : String(id));
                const isOptedIn = optedInClubs.includes(props.club._id);

                rows.push({
                    user_id: member.user_id,
                    name: member.user.first_name+ " " + member.user.last_name,
                    email: member.user.email,
                    opted_in: isOptedIn,
                    roles: member.capacities.map((c: string) => c.charAt(0).toUpperCase() + c.slice(1)).join(", "),
                    capacities: member.capacities,
                    actions: ""
                });
            }

            setRowData(rows);

            // Keep contacts rendering resilient even if summary endpoint is unavailable.
            try {
                const summary = await apiService.getClubCapacitySummary(props.club._id);
                setCapacityCounts({
                    [Constants.capacity_type.participant]: summary.participant || 0,
                    [Constants.capacity_type.volunteer]: summary.volunteer || 0,
                    [Constants.capacity_type.member]: summary.member || 0,
                    [Constants.capacity_type.admin]: summary.admin || 0,
                    [Constants.capacity_type.follower]: summary.follower || 0,
                    [Constants.capacity_type.donor]: summary.donor || 0
                });
            } catch {
                // Fallback to counts from the currently loaded page.
                const fallbackCounts: Record<string, number> = {
                    [Constants.capacity_type.participant]: 0,
                    [Constants.capacity_type.volunteer]: 0,
                    [Constants.capacity_type.member]: 0,
                    [Constants.capacity_type.admin]: 0,
                    [Constants.capacity_type.follower]: 0,
                    [Constants.capacity_type.donor]: 0
                };
                rows.forEach((row) => {
                    row.capacities.forEach((capacity) => {
                        fallbackCounts[capacity] = (fallbackCounts[capacity] || 0) + 1;
                    });
                });
                setCapacityCounts(fallbackCounts);
            }
        } catch (err: any) {
            //
        } finally {
            //
        }
    }, [props.club._id, selectedFilters]);

    useEffect(() => {
        if (!loaded) {
            setLoaded(true);
            getCapacities();
        }
    }, [loaded, getCapacities]);

  return (
    <div className={styles.MembersTab}>
        <div className={styles.filters}>
            <div className={styles.left}>
                {capacityFilters.map((filter) => (
                    <button
                        key={filter.key}
                        className={`${styles.filterTag} ${selectedFilters.includes(filter.key) ? styles.active : ''}`}
                        onClick={() => toggleFilter(filter.key)}
                        style={{
                            ...(selectedFilters.includes(filter.key) ? {
                                backgroundColor: capacityColors[filter.key],
                                borderColor: capacityColors[filter.key]
                            } : {
                                borderColor: capacityColors[filter.key],
                                color: capacityColors[filter.key]
                            })
                        }}
                    >
                        {filter.label} ({capacityCounts[filter.key] || 0})
                    </button>
                ))}
            </div>
            <div className={styles.right}>
                <div className={styles.settingsBtnWrap}>
                    <button onClick={toggleSettings} title="Volunteer Settings">Volunteer Settings</button>
                </div>
                <div className={`${styles.emailBtnWrap} ${!props.club.has_email_contacts_access ? styles.locked : ''}`}>
                    <button onClick={handleOpenSendMessage} title="Send Email">Send Email</button>
                    {!props.club.has_email_contacts_access && <div className={styles.lockIcon}></div>}
                </div>
                <div className={`${styles.exportBtnWrap} ${!props.club.has_export_contacts_access ? styles.locked : ''}`}>
                    <button onClick={handleExportContacts} title="Export to Excel">Export to Excel</button>
                    {!props.club.has_export_contacts_access && <div className={styles.lockIcon}></div>}
                </div>
                <div className={styles.bannedUsersBtnWrap}>
                    <button onClick={toggleBannedUsers} title="Banned Users">Banned Users</button>
                </div>
                <Button className={styles.Button} onClick={toggleAddContact}>Add Contact</Button>
            </div>
        </div>
        <div className={styles.tableWrap}>
            <div className={styles.tableWrapInner}>
                <AgGridReact
                    rowData={filteredRowData}
                    columnDefs={colDefs}
                    domLayout="normal"
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={[10, 25, 50, 100]}
                    gridOptions={{
                        defaultColDef: {
                            flex: 1,
                            minWidth: 100,
                            resizable: true
                        },
                        animateRows: true,
                        rowHeight: 48,
                        headerHeight: 48,
                        overlayNoRowsTemplate: '<span>No Contacts</span>',
                        suppressExcelExport: true,
                        suppressCsvExport: true
                    }}
                />
            </div>
        </div>
        <AddContactModal
            isShown={isAddContactShown}
            hide={toggleAddContact}
            clubId={props.club._id}
            onSuccess={() => { setLoaded(false); }}
        />
        <VolunteerSettingsModal
            isShown={isSettingsShown}
            hide={toggleSettings}
            clubId={props.club._id}
        />
        {selectedUser && (
            <EditCapacitiesModal
                isShown={isEditShown}
                hide={toggleEdit}
                clubId={props.club._id}
                user={selectedUser}
                onSave={() => { setLoaded(false); }}
            />
        )}
        <Modal
            className="Confirmation"
            isShown={isRemoveShown}
            hide={toggleRemove}
            modalContent={
                <Confirm
                    message={
                        <>
                            <p>This will remove <strong>{memberToRemove?.name}</strong> from all roles in this club.</p>
                            <p>They will no longer have any access or permissions.</p>
                        </>
                    }
                    onConfirm={confirmRemoveMember}
                    onCancel={toggleRemove}
                    buttonsDisabled={isRemoving}
                />
            }
        />
        <Modal
            isShown={isSendMessageShown}
            hide={toggleSendMessage}
            className="sendMessageModal"
            modalContent={
                <SendMessage
                    handleSendMessage={handleBulkSendMessage}
                    subject="Message to Club Contacts"
                    type={SendMessageType.ClubContacts}
                    title="Send Email"
                    isSending={isSending}
                    topText={
                        <div>
                            <p>
                                This message will be sent to <strong>{optInCounts.optedIn}</strong> of <strong>{optInCounts.total}</strong> contact{optInCounts.total !== 1 ? 's' : ''}.
                                {selectedFilters.length > 0 && (
                                    <><br />(Filtered by: {selectedFilters.map(f => f.charAt(0).toUpperCase() + f.slice(1)).join(', ')})</>
                                )}
                            </p>
                            {optInCounts.optedOut > 0 && (
                                <p style={{ color: '#6B7280', fontSize: '14px', marginTop: '8px' }}>
                                    ({optInCounts.optedOut} contact{optInCounts.optedOut !== 1 ? 's have' : ' has'} not opted in to receive communications)
                                </p>
                            )}
                        </div>
                    }
                />
            }
        />
        <Modal
            isShown={isSendIndividualShown}
            hide={toggleSendIndividual}
            className="sendMessageModal"
            modalContent={
                <SendMessage
                    handleSendMessage={handleIndividualSendMessage}
                    subject={`Message to ${messageRecipient?.name || 'Contact'}`}
                    type={SendMessageType.ClubContacts}
                    title="Send Email"
                    isSending={isSending}
                    topText={<p>Email will be sent to: <strong>{messageRecipient?.email}</strong></p>}
                />
            }
        />
        <BannedUsersModal
            isShown={isBannedUsersShown}
            hide={toggleBannedUsers}
            clubId={props.club._id}
        />
    </div>
  );
};

export default MembersTab;
